/*
* Copyright (C) Regione Lombardia
*/
import { AfterContentChecked, AfterViewInit, Component, ElementRef, OnDestroy, OnInit, Renderer2, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
    AnagraficaComuni,
    DatalistAnagrafica,
    FormDataSessionEnum,
    Opera,
    OperaStatoEnum,
    Paginator,
    Sorting,
    SortingDirectionEnum
} from '@models';
import { NgbTooltip, NgbTypeaheadSelectItemEvent } from '@ng-bootstrap/ng-bootstrap';
import { OperaService } from '@shared/services/opera.service';
import { UtenteService } from '@shared/services/utente.service';
import { UtilsService } from '@shared/services/utils.service';
import { ViewerService } from '@shared/services/viewer.service';
import {
    debounceTime,
    distinctUntilChanged,
    map,
    Observable,
    OperatorFunction
} from 'rxjs';


//declare var $: any;


@Component({
    selector: 'app-pages-gestione-opera-ricerca-opera',
    templateUrl: './ricerca-opera.component.html',
})
export class PagesGestioneOperaRicercaOperaComponent implements OnInit, OnDestroy, AfterViewInit {
    tooltipEsporta = "Esporta soltanto le opere «verificate» corrispondenti ai filtri settati.";


    public datalist: DatalistAnagrafica;
    public comuniFiltered: AnagraficaComuni[] = [];
    public pageType: string;
    public criteriaForm: FormGroup;
    public tableData: Opera[] = [];
    public tableSort: Sorting[] = [];
    public paginator: Paginator = new Paginator();
    public windowClosed: boolean = false;
    @ViewChild('inputComuni') inputComuni: ElementRef;


    constructor(
        public operaService: OperaService,
        public utenteService: UtenteService,
        private viewerService: ViewerService,
        private utilsService: UtilsService,
        private router: Router,
        private route: ActivatedRoute,
        private renderer: Renderer2
    ) {
        this.pageType = this.route.snapshot.data['pageType'];
        this.operaService.pageTypeSession = this.route.snapshot.data['pageType'];
    }

    ngOnInit() {
        this.datalist = this.route.snapshot.data['additionalInformation'].datalist;
        this.tableSort = [
            { field: 'idOpera', direction: SortingDirectionEnum.EMPTY },
            { field: 'denProv', direction: SortingDirectionEnum.EMPTY },
            { field: 'denComune', direction: SortingDirectionEnum.EMPTY },
        ];
        const sessionForm = this.utilsService.getFormDataSession(FormDataSessionEnum.OPERA_RICERCA);
        this.criteriaForm = this.operaService.generateFormRicercaOpera(sessionForm);
        this.submitForm();
        this.criteriaForm.controls['idStato'].setValue(this.pageType === 'approva-opera' ? OperaStatoEnum.COMPLETATA : null);
        this.searchComuniByProvincia();
    }


    ngOnDestroy() {
        document.removeEventListener('visibilitychange', this.onVisiblePage);
    }

    /* Typeahead Comuni */
    public filterComuni: OperatorFunction<string, readonly any[]> = (text$: Observable<string>) => text$.pipe(
        debounceTime(200),
        distinctUntilChanged(),
        map((term) => {
            if(!term) {
                this.criteriaForm.get('codIstat')?.reset();
            }
            return this.comuniFiltered.filter((i: AnagraficaComuni) => new RegExp(term, 'mi').test(i.descrizione)).slice(0, 10)
        }),
    );
    public selectItemComuni(event: NgbTypeaheadSelectItemEvent) {
        this.criteriaForm.controls['codIstat'].setValue(event.item.id);
    }
    public searchComuniByProvincia() {
        if (this.criteriaForm.value.codProv === null) {
            this.criteriaForm.controls['codIstat'].setValue(null);
            this.criteriaForm.controls['comune'].setValue(null);
            this.comuniFiltered = [...(this.datalist.comuni || [])];
        } else {
            this.comuniFiltered = [...(this.datalist.comuni || [])].filter((i: AnagraficaComuni) =>
                i.extra1 == this.criteriaForm.value.codProv
            );
        }
    }

    public submitForm(fromBtn?: boolean) {
        if(fromBtn) {
            this.paginator.resetPaginator();
        }
        this.utilsService.setFormDataSession(FormDataSessionEnum.OPERA_RICERCA, this.criteriaForm.getRawValue());
        if(this.pageType === 'ricerca') {
            if (this.pageType === 'ricerca') {
                const utente = this.utenteService.utente;
                const idUtente = utente.id;
                const ruolo = utente.ruolo;

                if (ruolo === 2) {
                    // Operatore → chiama il nuovo endpoint con ID utente
                    this.operaService.ricercaOperaOperatore(
                        this.criteriaForm.getRawValue(),
                        idUtente,
                        this.paginator.getQueryPaginator(this.tableSort)
                    ).subscribe(response => {
                        this.tableData = response.content;
                        this.paginator.setTotalPage(response.totalElements);
                    });
                } else {
                    // Amministratore → chiama la ricerca standard
                    this.operaService.search(
                        this.criteriaForm.getRawValue(),
                        this.paginator.getQueryPaginator(this.tableSort)
                    ).subscribe(response => {
                        this.tableData = response.content;
                        this.paginator.setTotalPage(response.totalElements);
                    });
                }
            }

        } else {
            this.operaService.searchApprovaOpera(this.criteriaForm.getRawValue(), this.paginator.getQueryPaginator(this.tableSort)).subscribe(response => {
                this.tableData = response.content;
                this.paginator.setTotalPage(response.totalElements);
            });
        }

    }

    public resetForm() {
        this.paginator.resetPaginator();
        this.tableData = [];
        this.criteriaForm.reset();
        this.criteriaForm.controls['idStato'].setValue(this.pageType === 'approva-opera' ? OperaStatoEnum.COMPLETATA : null);
        this.utilsService.setFormDataSession(FormDataSessionEnum.OPERA_RICERCA, this.criteriaForm.getRawValue());
        this.searchComuniByProvincia();
    }

    public sortTable(ordinamento: any, index: number) {
        this.tableSort[index].direction = ordinamento.direction ? ordinamento.direction : ordinamento;
        this.submitForm();
    }

    public onPageChange($event: number) {
        this.paginator.page = $event;
        this.submitForm();
    }

    public goToOpera(idOpera: number) {
        this.router.navigate(['../dettaglio', idOpera], { relativeTo: this.route });
    }

    public goToViewer(idOpera: number, codIstat: string, idTipologiaOpera: number) {
        document.addEventListener('visibilitychange', this.onVisiblePage);
        this.viewerService.getUrlViewerOpera(idOpera, codIstat, idTipologiaOpera).subscribe((response: any) => {
                const win: Window | null =  window.open(response.urlViewer, 'blank');
                win?.addEventListener('beforeunload', (ev)=> {
                    this.windowClosed = true;
                });
            }
        )
    }

    public searchExport() {
        this.operaService.searchExport(this.criteriaForm.getRawValue(), this.paginator.getQueryPaginatorExport(this.tableSort)).subscribe();
    }

    public comuneFormatter = (result: any) => {
        if(result.descrizione) {
            return result.descrizione;
        }
        return this.comuniFiltered.find(f=> f.id === result)?.descrizione;
    };


    onVisiblePage = () => {
        if (document.visibilityState === "visible") {
            this.submitForm();
        } else if(this.windowClosed) {
            document.removeEventListener('visibilitychange', this.onVisiblePage);
            this.windowClosed = false;
        }
    }

    //Accessibility

    ngAfterViewInit(): void {
        if (this.inputComuni !== undefined ) {
            this.renderer.removeAttribute(this.inputComuni.nativeElement, 'aria-multiline');
        }
    }

    //   ngAfterContentChecked(): void {

    //    $('[data-toogle="tooltip"]').tooltip();

    // }


}
